/**
 * Garment Order composer — create AND edit (ADR-0039, ADR-0041, ADR-0043).
 * Buyer, PO details, delivery, and one or more GARMENTS — a buyer PO
 * routinely covers several styles (a tee AND a polo), each with its own
 * name/spec and its own colour × size grid (colours as rows, sizes as
 * columns — the same ratio-chart grammar as the Style File composer and the
 * order view). Picking a garment's type seeds its size columns from the
 * type's template; further sizes add from a dropdown of that type's standard
 * run (free text only via the Custom size escape hatch) and remove via the
 * column's ✕. Empty (qty 0) cells are dropped server-side, which is what
 * keeps each garment's grid sparse — a colour that skips a size simply has
 * no cell.
 *
 * Edit mode: pass `initial` (the loaded `garmentOrders.get` detail) — the
 * form prefills every garment and submits `garmentOrders.update` instead
 * (garments replace wholesale). Editable while open or accepted (ADR-0042);
 * the server enforces it, the route gates entry.
 *
 * Quantities are keyed by a STABLE per-row id, not the colour's array index:
 * index keys silently re-associate quantities to the wrong colour when a row
 * is removed. Garments themselves are keyed the same way.
 *
 * @see style-composer-page.tsx — the colour × size ratio grid this mirrors
 */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, ChevronDown, Palette, Plus, Ruler, Shirt, X } from "lucide-react";
import {
  Alert,
  Button,
  Combobox,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRoot,
  DropdownMenuTrigger,
  PromptDialog,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@flint/ui";
import { trpc } from "../../../lib/trpc.js";
import { buildGarmentTypeGroups, garmentTypeLabel } from "../lib/garment-type.js";

/** Prefill shape for edit mode — the `garmentOrders.get` detail. */
export interface GarmentOrderComposerInitial {
  id: string;
  orderNo: string;
  buyerId: string;
  description: string | null;
  buyerRef: string | null;
  poNumber: string | null;
  poDate: string | null;
  deliveryDate: string | null;
  notes: string | null;
  items: Array<{
    garmentType: string;
    name: string;
    spec: string | null;
    lines: Array<{ colour: string; colourHex: string | null; size: string; qty: number }>;
  }>;
}

/** A colour row. `id` is local-only — it keys qty so removal can't shift cells. */
interface ColourRow {
  id: string;
  colour: string;
  colourHex: string;
}

/** One garment card. `id` is local-only, same reason as `ColourRow.id`. */
interface ItemState {
  id: string;
  garmentType: string;
  name: string;
  spec: string;
  colours: ColourRow[];
  sizes: string[];
  /** Keyed `${colourRowId}|${size}`. */
  qty: Record<string, string>;
  sizesTouched: boolean;
}

let rowSeq = 0;
const nextRowId = (prefix: string) => `${prefix}${(rowSeq += 1)}`;

function blankItem(): ItemState {
  return {
    id: nextRowId("i"),
    garmentType: "",
    name: "",
    spec: "",
    colours: [{ id: nextRowId("c"), colour: "", colourHex: "" }],
    sizes: [],
    qty: {},
    sizesTouched: false,
  };
}

/** Rebuild one garment's state from its saved grid, preserving first-seen order. */
function itemFromInitial(item: GarmentOrderComposerInitial["items"][number]): ItemState {
  const colours: ColourRow[] = [];
  const sizes: string[] = [];
  const qty: Record<string, string> = {};
  for (const line of item.lines) {
    let row = colours.find((c) => c.colour === line.colour);
    if (!row) {
      row = { id: nextRowId("c"), colour: line.colour, colourHex: line.colourHex ?? "" };
      colours.push(row);
    }
    if (!sizes.includes(line.size)) sizes.push(line.size);
    qty[`${row.id}|${line.size}`] = String(line.qty);
  }
  if (colours.length === 0) colours.push({ id: nextRowId("c"), colour: "", colourHex: "" });
  return {
    id: nextRowId("i"),
    garmentType: item.garmentType,
    name: item.name,
    spec: item.spec ?? "",
    colours,
    sizes,
    qty,
    sizesTouched: true,
  };
}

function cellQty(item: ItemState, rowId: string, size: string): number {
  return Number.parseInt(item.qty[`${rowId}|${size}`] ?? "", 10) || 0;
}

function rowTotal(item: ItemState, rowId: string): number {
  return item.sizes.reduce((sum, size) => sum + cellQty(item, rowId, size), 0);
}

function colTotal(item: ItemState, size: string): number {
  return item.colours.reduce((sum, row) => sum + cellQty(item, row.id, size), 0);
}

function itemTotalQty(item: ItemState): number {
  return item.colours.reduce((sum, row) => sum + rowTotal(item, row.id), 0);
}

/** A named colour with no quantity is a half-filled row — block submit on it. */
function itemHasNamedColour(item: ItemState): boolean {
  return item.colours.some((c) => c.colour.trim() !== "");
}

/** Flatten one garment's grid into the sparse cells the wire expects. */
function parsedLinesFor(item: ItemState): Array<{
  colour: string;
  colourHex: string | null;
  size: string;
  qty: number;
}> {
  const cells: Array<{ colour: string; colourHex: string | null; size: string; qty: number }> = [];
  for (const row of item.colours) {
    const colour = row.colour.trim();
    if (colour === "") continue;
    for (const size of item.sizes) {
      const n = cellQty(item, row.id, size);
      if (n <= 0) continue;
      cells.push({ colour, colourHex: row.colourHex.trim() || null, size, qty: n });
    }
  }
  return cells;
}

export function GarmentOrderComposerPage({
  initial,
}: {
  initial?: GarmentOrderComposerInitial;
}) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const isEdit = initial !== undefined;

  const buyers = trpc.customers.list.useQuery({ limit: 100 });
  const templates = trpc.styles.templates.useQuery();
  const garmentTypeGroups = useMemo(
    () => buildGarmentTypeGroups(templates.data ?? []),
    [templates.data],
  );

  const [buyerId, setBuyerId] = useState(initial?.buyerId ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [buyerRef, setBuyerRef] = useState(initial?.buyerRef ?? "");
  // PO details are OPTIONAL (ADR-0042) — acceptance never waits on paperwork,
  // and the buyer's PO routinely lands after the order is logged.
  const [poNumber, setPoNumber] = useState(initial?.poNumber ?? "");
  const [poDate, setPoDate] = useState(initial?.poDate ?? "");
  const [deliveryDate, setDeliveryDate] = useState(initial?.deliveryDate ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");

  // One card per garment (ADR-0043) — seed once from `initial`, then live here.
  const seedItems = useMemo(
    () => (initial ? initial.items.map(itemFromInitial) : [blankItem()]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [items, setItems] = useState<ItemState[]>(seedItems);
  /** Which garment's Custom-size PromptDialog is open, if any. */
  const [sizeDialogFor, setSizeDialogFor] = useState<string | null>(null);

  const updateItem = (itemId: string, patch: Partial<ItemState>) =>
    setItems((its) => its.map((it) => (it.id === itemId ? { ...it, ...patch } : it)));

  const addItem = () => setItems((its) => [...its, blankItem()]);
  const removeItem = (itemId: string) =>
    setItems((its) => (its.length === 1 ? its : its.filter((it) => it.id !== itemId)));

  /** Picking a type seeds untouched size columns from that garment's template. */
  const onPickType = (itemId: string, key: string) => {
    setItems((its) =>
      its.map((it) => {
        if (it.id !== itemId) return it;
        if (it.sizesTouched) return { ...it, garmentType: key };
        const template = templates.data?.find((t) => t.key === key);
        if (template && template.sizes.length > 0) {
          return { ...it, garmentType: key, sizes: [...template.sizes], qty: {} };
        }
        return { ...it, garmentType: key };
      }),
    );
  };

  /**
   * Add a size column to one garment. Used by both the dropdown (a known
   * template size) and the custom-size PromptDialog, so it stays
   * duplicate-safe either way — on a duplicate it throws, which keeps the
   * prompt dialog open.
   */
  const addSize = (itemId: string, raw: string) => {
    const next = raw.trim();
    if (next === "") throw new Error("empty size");
    const item = items.find((it) => it.id === itemId);
    if (item?.sizes.some((s) => s.toLowerCase() === next.toLowerCase())) {
      toast.error("That size already exists", { description: `“${next}” is already a column.` });
      throw new Error("duplicate size"); // keep the dialog open
    }
    updateItem(itemId, { sizesTouched: true, sizes: [...(item?.sizes ?? []), next] });
  };

  const removeSize = (itemId: string, size: string) => {
    setItems((its) =>
      its.map((it) => {
        if (it.id !== itemId) return it;
        return {
          ...it,
          sizesTouched: true,
          sizes: it.sizes.filter((x) => x !== size),
          // Drop that column's cell for every colour row.
          qty: Object.fromEntries(
            Object.entries(it.qty).filter(([key]) => !key.endsWith(`|${size}`)),
          ),
        };
      }),
    );
  };

  const addColour = (itemId: string) =>
    updateItem(itemId, {
      colours: [
        ...(items.find((it) => it.id === itemId)?.colours ?? []),
        { id: nextRowId("c"), colour: "", colourHex: "" },
      ],
    });

  const removeColour = (itemId: string, colourId: string) => {
    setItems((its) =>
      its.map((it) => {
        if (it.id !== itemId || it.colours.length === 1) return it;
        return {
          ...it,
          colours: it.colours.filter((c) => c.id !== colourId),
          qty: Object.fromEntries(
            Object.entries(it.qty).filter(([key]) => !key.startsWith(`${colourId}|`)),
          ),
        };
      }),
    );
  };

  const setColourField = (itemId: string, colourId: string, patch: Partial<ColourRow>) =>
    setItems((its) =>
      its.map((it) =>
        it.id === itemId
          ? {
              ...it,
              colours: it.colours.map((c) => (c.id === colourId ? { ...c, ...patch } : c)),
            }
          : it,
      ),
    );

  const setCellQty = (itemId: string, colourId: string, size: string, raw: string) => {
    const v = raw.replace(/[^\d]/g, "");
    setItems((its) =>
      its.map((it) =>
        it.id === itemId
          ? { ...it, sizesTouched: true, qty: { ...it.qty, [`${colourId}|${size}`]: v } }
          : it,
      ),
    );
  };

  const totalQty = useMemo(() => items.reduce((sum, it) => sum + itemTotalQty(it), 0), [items]);
  const everyItemValid = items.every(
    (it) => it.garmentType !== "" && it.name.trim() !== "" && itemHasNamedColour(it),
  );

  const create = trpc.garmentOrders.create.useMutation({
    onSuccess: async (result) => {
      await utils.garmentOrders.list.invalidate();
      toast.success(`Order ${result.orderNo} logged`, {
        description: "Link a Style File per garment when engineering picks it up.",
      });
      void navigate({ to: "/merchandising/orders" });
    },
    onError: (err) => {
      toast.error("Couldn't log the order", { description: err.message });
    },
  });

  const update = trpc.garmentOrders.update.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.garmentOrders.list.invalidate(),
        initial ? utils.garmentOrders.get.invalidate({ id: initial.id }) : Promise.resolve(),
      ]);
      toast.success(`Order ${initial?.orderNo} updated`);
      if (initial) void navigate({ to: "/merchandising/orders/$orderId", params: { orderId: initial.id } });
    },
    onError: (err) => {
      toast.error("Couldn't save the changes", { description: err.message });
    },
  });

  const pending = create.isPending || update.isPending;
  const canSubmit = buyerId !== "" && everyItemValid && totalQty > 0 && !pending;

  const submit = () => {
    if (!canSubmit) return;
    const payload = {
      buyerId,
      description: description.trim() === "" ? null : description.trim(),
      buyerRef: buyerRef.trim() === "" ? null : buyerRef.trim(),
      poNumber: poNumber.trim() === "" ? null : poNumber.trim(),
      poDate: poDate === "" ? null : poDate,
      deliveryDate: deliveryDate === "" ? null : deliveryDate,
      notes: notes.trim() === "" ? null : notes.trim(),
      items: items.map((it) => ({
        garmentType: it.garmentType,
        name: it.name.trim(),
        spec: it.spec.trim() === "" ? null : it.spec.trim(),
        lines: parsedLinesFor(it),
      })),
    };
    if (initial) {
      update.mutate({ id: initial.id, ...payload });
    } else {
      create.mutate(payload);
    }
  };

  const inputClass =
    "w-full rounded-md border border-line bg-paper px-3 py-2 text-sm text-ink placeholder:text-muted focus:border-sky-400 focus:outline-none";

  return (
    <div className="flex min-h-full flex-col">
      {/* Sticky head — mirrors the invoice composer shell (fixed header +
       *  scrolling centred body + fixed footer). No preview column here, so
       *  the form card is centred with no left/right offset. */}
      <header className="sticky top-0 z-20 flex min-h-14 shrink-0 flex-col items-stretch gap-3 border-b border-line bg-paper px-6 py-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="flex min-w-0 flex-1 items-center gap-3.5">
          <Link
            to="/merchandising/orders"
            className="grid h-9 w-9 shrink-0 place-items-center rounded-sm border border-line bg-paper text-ink-2 transition-colors hover:bg-bg"
            aria-label="Back to orders"
          >
            <ArrowLeft size={16} strokeWidth={2.2} />
          </Link>
          <div className="min-w-0">
            <h1 className="text-base font-bold leading-tight text-ink">
              {isEdit ? `Edit ${initial?.orderNo}` : "New order"}
            </h1>
            <p className="mt-0.5 text-xs text-muted">
              {isEdit
                ? "Correct the commercial frame — editable until every garment's Style File is linked."
                : "Log what the buyer committed to — one card per garment. Style Files come later."}
            </p>
          </div>
        </div>
      </header>

      {/* Scrolling body — centred form (max-w-3xl); the sticky header/footer
       *  span full width. */}
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-5 px-6 py-5">
        {create.error || update.error ? (
          <Alert
            intent="bad"
            title={isEdit ? "Couldn't save the changes" : "Couldn't log the order"}
            description={(create.error ?? update.error)?.message ?? ""}
          />
        ) : null}

      <div className="flex flex-col gap-4 rounded-md border border-line bg-paper p-5 shadow-sm">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted">Buyer *</span>
            <Select value={buyerId} onValueChange={setBuyerId} disabled={buyers.isLoading}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder={buyers.isLoading ? "Loading…" : "Select buyer"} />
              </SelectTrigger>
              <SelectContent>
                {(buyers.data?.items ?? []).map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted">Description</span>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Spring drop — 2 styles (optional)"
              maxLength={240}
              className={inputClass}
            />
          </label>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted">Buyer reference</span>
            <input
              value={buyerRef}
              onChange={(e) => setBuyerRef(e.target.value)}
              placeholder="Their internal ref (optional)"
              maxLength={40}
              className={inputClass}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted">Buyer PO number</span>
            <input
              value={poNumber}
              onChange={(e) => setPoNumber(e.target.value)}
              placeholder="Their formal PO no. (optional)"
              maxLength={40}
              className={inputClass}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted">Buyer PO date</span>
            <input
              type="date"
              value={poDate}
              onChange={(e) => setPoDate(e.target.value)}
              className={inputClass}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-muted">Delivery date</span>
            <input
              type="date"
              value={deliveryDate}
              onChange={(e) => setDeliveryDate(e.target.value)}
              className={inputClass}
            />
          </label>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-ink">
            Garments
            <span className="ml-2 font-normal text-muted">
              {items.length} · {totalQty.toLocaleString("en-IN")} pcs total
            </span>
          </h2>
          <Button type="button" intent="ghost" size="sm" className="border border-line bg-paper" onClick={addItem}>
            <Shirt size={14} aria-hidden />
            Add garment
          </Button>
        </div>

        {items.map((item, itemIndex) => {
          const unusedTemplateSizes = (() => {
            const template = templates.data?.find((t) => t.key === item.garmentType);
            if (!template) return [];
            const have = new Set(item.sizes.map((s) => s.toLowerCase()));
            return template.sizes.filter((s) => !have.has(s.toLowerCase()));
          })();
          const total = itemTotalQty(item);
          const hasNamedColour = itemHasNamedColour(item);

          return (
            <div
              key={item.id}
              className="flex flex-col gap-4 rounded-md border border-line bg-paper p-5 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <span className="mt-1 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-raised text-xs font-bold text-muted">
                  {itemIndex + 1}
                </span>
                <div className="grid flex-1 grid-cols-1 gap-4 sm:grid-cols-2">
                  <label className="flex flex-col gap-1.5">
                    <span className="text-xs font-medium text-muted">Garment type *</span>
                    <Combobox
                      value={item.garmentType}
                      onValueChange={(key) => onPickType(item.id, key)}
                      groups={garmentTypeGroups}
                      placeholder={templates.isLoading ? "Loading…" : "Select type"}
                      searchPlaceholder="Search garment types…"
                      emptyMessage="No matching garment type."
                      disabled={templates.isLoading}
                    />
                  </label>
                  <label className="flex flex-col gap-1.5">
                    <span className="text-xs font-medium text-muted">What did they order? *</span>
                    <input
                      value={item.name}
                      onChange={(e) => updateItem(item.id, { name: e.target.value })}
                      placeholder="e.g. Crew-neck tee, 180gsm combed cotton"
                      maxLength={160}
                      className={inputClass}
                    />
                  </label>
                </div>
                {items.length > 1 ? (
                  <button
                    type="button"
                    aria-label={`Remove garment ${itemIndex + 1}`}
                    onClick={() => removeItem(item.id)}
                    className="mt-1 grid h-6 w-6 shrink-0 place-items-center rounded text-muted transition hover:bg-bad-50 hover:text-bad"
                  >
                    <X size={14} aria-hidden />
                  </button>
                ) : null}
              </div>

              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-muted">Spec</span>
                <input
                  value={item.spec}
                  onChange={(e) => updateItem(item.id, { spec: e.target.value })}
                  placeholder="Fabric, wash, trims, packing notes (optional)"
                  maxLength={2000}
                  className={inputClass}
                />
              </label>

              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-ink">Colour × size breakdown *</h3>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted">
                      Total{" "}
                      <strong className="font-mono font-bold tabular-nums text-ink">
                        {total.toLocaleString("en-IN")}
                      </strong>{" "}
                      pcs
                    </span>
                    <Button
                      type="button"
                      intent="ghost"
                      size="sm"
                      className="border border-line bg-paper"
                      onClick={() => addColour(item.id)}
                    >
                      <Palette size={14} aria-hidden />
                      Add colour
                    </Button>
                    <DropdownMenuRoot>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          intent="ghost"
                          size="sm"
                          className="border border-line bg-paper"
                          disabled={item.garmentType === "" || unusedTemplateSizes.length === 0}
                        >
                          <Ruler size={14} aria-hidden />
                          Add size
                          <ChevronDown size={13} aria-hidden />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>{garmentTypeLabel(item.garmentType)} sizes</DropdownMenuLabel>
                        {unusedTemplateSizes.map((size) => (
                          <DropdownMenuItem key={size} onSelect={() => addSize(item.id, size)}>
                            <span className="font-mono">{size}</span>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenuRoot>
                    <Button
                      type="button"
                      intent="ghost"
                      size="sm"
                      className="border border-line bg-paper"
                      disabled={item.garmentType === ""}
                      onClick={() => setSizeDialogFor(item.id)}
                    >
                      <Ruler size={14} aria-hidden />
                      Custom size
                    </Button>
                  </div>
                </div>

                {item.sizes.length === 0 ? (
                  <div className="flex flex-col items-center gap-1 rounded-md border border-dashed border-line bg-raised px-4 py-8 text-center">
                    <Ruler className="mb-1 text-muted" size={20} aria-hidden />
                    <p className="text-sm font-medium text-ink-2">No sizes yet</p>
                    <p className="max-w-sm text-xs text-muted">
                      Pick a <span className="font-medium text-ink-2">garment type</span> above to
                      seed the standard size run, or add sizes one at a time.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-md border border-line">
                    <table className="w-full min-w-[520px] border-collapse text-sm">
                      <thead>
                        <tr className="bg-raised text-xs uppercase tracking-wide text-muted">
                          <th className="w-52 px-3 py-2.5 text-left font-medium">Colour</th>
                          {item.sizes.map((size) => (
                            <th key={size} className="px-2 py-2.5 text-center font-medium">
                              <div className="inline-flex items-center gap-1">
                                <span className="font-mono normal-case text-ink-2">{size}</span>
                                <button
                                  type="button"
                                  aria-label={`Remove size ${size}`}
                                  onClick={() => removeSize(item.id, size)}
                                  className="grid h-4 w-4 place-items-center rounded text-muted transition hover:bg-bad-50 hover:text-bad"
                                >
                                  <X size={11} aria-hidden />
                                </button>
                              </div>
                            </th>
                          ))}
                          <th className="border-l border-line px-3 py-2.5 text-right font-medium">
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {item.colours.map((row) => (
                          <tr key={row.id} className="border-t border-line">
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-1.5">
                                <input
                                  type="color"
                                  aria-label={`Swatch for colour ${row.colour || "unnamed"}`}
                                  value={row.colourHex || "#1e3a8a"}
                                  onChange={(e) =>
                                    setColourField(item.id, row.id, { colourHex: e.currentTarget.value })
                                  }
                                  className="h-7 w-7 shrink-0 cursor-pointer rounded-sm border border-line bg-paper p-0.5"
                                />
                                <input
                                  aria-label="Colour name"
                                  value={row.colour}
                                  placeholder="e.g. Navy"
                                  maxLength={40}
                                  onChange={(e) =>
                                    setColourField(item.id, row.id, { colour: e.currentTarget.value })
                                  }
                                  className="h-8 w-full rounded-sm border border-line bg-paper px-2 text-sm text-ink-2 placeholder:text-line-2 focus:border-sky-600 focus:outline-none focus:ring-2 focus:ring-sky-100"
                                />
                                {item.colours.length > 1 ? (
                                  <button
                                    type="button"
                                    aria-label={`Remove colour ${row.colour || "unnamed"}`}
                                    onClick={() => removeColour(item.id, row.id)}
                                    className="grid h-5 w-5 shrink-0 place-items-center rounded text-muted transition hover:bg-bad-50 hover:text-bad"
                                  >
                                    <X size={12} aria-hidden />
                                  </button>
                                ) : null}
                              </div>
                            </td>
                            {item.sizes.map((size) => (
                              <td key={size} className="px-1.5 py-2 text-center">
                                <input
                                  inputMode="numeric"
                                  aria-label={`Quantity for ${row.colour || "unnamed colour"} size ${size}`}
                                  value={item.qty[`${row.id}|${size}`] ?? ""}
                                  placeholder="0"
                                  onChange={(e) =>
                                    setCellQty(item.id, row.id, size, e.currentTarget.value)
                                  }
                                  className="h-8 w-16 rounded-sm border border-line bg-paper px-1 text-center font-mono text-sm text-ink-2 placeholder:text-line-2 focus:border-sky-600 focus:outline-none focus:ring-2 focus:ring-sky-100"
                                />
                              </td>
                            ))}
                            <td className="border-l border-line px-3 py-2 text-right font-mono text-sm font-bold text-ink">
                              {rowTotal(item, row.id).toLocaleString("en-IN")}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t border-line bg-raised">
                          <td className="px-3 py-2 text-xs uppercase tracking-wide text-muted">Total</td>
                          {item.sizes.map((size) => (
                            <td
                              key={size}
                              className="px-2 py-2 text-center font-mono text-xs font-bold text-ink-2"
                            >
                              {colTotal(item, size) > 0 ? colTotal(item, size).toLocaleString("en-IN") : "—"}
                            </td>
                          ))}
                          <td className="border-l border-line px-3 py-2 text-right font-mono text-sm font-bold text-ink">
                            {total.toLocaleString("en-IN")}
                          </td>
                        </tr>
                        <tr className="border-t border-line bg-raised">
                          <td className="px-3 py-2 text-xs uppercase tracking-wide text-muted">Share</td>
                          {item.sizes.map((size) => {
                            const n = colTotal(item, size);
                            return (
                              <td key={size} className="px-2 py-2 text-center font-mono text-xs text-muted">
                                {total > 0 && n > 0 ? `${Math.round((n / total) * 100)}%` : "—"}
                              </td>
                            );
                          })}
                          <td className="border-l border-line px-3 py-2 text-right font-mono text-xs text-muted">
                            {total > 0 ? "100%" : "—"}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
                {!hasNamedColour && item.sizes.length > 0 ? (
                  <p className="text-xs text-muted">Name at least one colour for this garment.</p>
                ) : null}
              </div>

              {/* Custom-size dialog — shared HTML PromptDialog (never window.prompt). */}
              <PromptDialog
                open={sizeDialogFor === item.id}
                onOpenChange={(open) => setSizeDialogFor(open ? item.id : null)}
                title="Add a size"
                description={
                  <>
                    Adds a new column to this garment's breakdown — e.g.{" "}
                    <span className="font-mono">XXL</span>, <span className="font-mono">48</span>, or{" "}
                    <span className="font-mono">12-13Y</span>.
                  </>
                }
                label="Size label"
                placeholder="e.g. XXL"
                confirmLabel="Add size"
                onConfirm={(raw) => addSize(item.id, raw)}
              />
            </div>
          );
        })}

        <Button
          type="button"
          intent="ghost"
          className="justify-center border border-dashed border-line bg-paper"
          onClick={addItem}
        >
          <Plus size={15} aria-hidden />
          Add another garment
        </Button>
      </div>

      <div className="flex flex-col gap-3 rounded-md border border-line bg-paper p-5 shadow-sm">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-muted">Notes</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Anything the factory should know (optional)"
            rows={3}
            maxLength={2000}
            className={`${inputClass} resize-y`}
          />
        </label>
      </div>

      </div>

      {/* Sticky footer — hint on the left, actions on the right. */}
      <footer className="sticky bottom-0 z-10 flex shrink-0 flex-wrap items-center gap-2.5 border-t border-line bg-paper px-6 py-3 shadow-lg">
        <p className="min-w-0 flex-1 text-xs text-muted">
          {canSubmit ? (
            <>
              <strong className="text-ink">Ready to log.</strong> Buyer and garment quantities are
              set.
            </>
          ) : (
            "Pick a buyer and add at least one garment with quantities."
          )}
        </p>
        <div className="flex items-center gap-2">
          <Button intent="ghost" asChild>
            {isEdit && initial ? (
              <Link to="/merchandising/orders/$orderId" params={{ orderId: initial.id }}>
                Cancel
              </Link>
            ) : (
              <Link to="/merchandising/orders">Cancel</Link>
            )}
          </Button>
          <Button disabled={!canSubmit} onClick={submit}>
            {isEdit
              ? pending
                ? "Saving…"
                : "Save changes"
              : pending
                ? "Logging order…"
                : "Log order"}
          </Button>
        </div>
      </footer>
    </div>
  );
}
